
import { useRef, useEffect, useCallback } from 'react';
import type { Question } from '@/types/quiz';

interface QuestionSnapshot extends Question {
  userAnswer?: number | null;
  shownAtIndex?: number;
}

export function useQuizSessionHistory() {
  // Use a stable ref for storing snapshots to prevent re-renders
  const snapshotsRef = useRef<QuestionSnapshot[]>([]);
  const resultSubmittedRef = useRef<boolean>(false);
  const sessionStartTimeRef = useRef<number>(Date.now());
  const initializedRef = useRef<boolean>(false);
  const isActivelyShownRef = useRef<Record<string | number, boolean>>({});

  // Initialize global snapshots array if needed (only once)
  useEffect(() => {
    if (!initializedRef.current) {
      if (typeof window !== 'undefined') {
        if (!(window as any).questionSnapshots) {
          (window as any).questionSnapshots = { current: [] };
          console.log("Initialized global question snapshots array");
        }
        
        // Point our local ref to the global store
        snapshotsRef.current = (window as any).questionSnapshots?.current || [];
        initializedRef.current = true;
      }
    }
    
    return () => {
      // Don't clear snapshots on unmount - we want to keep them for the entire session
    };
  }, []);

  // Reset submission status
  const resetSubmissionStatus = useCallback(() => {
    resultSubmittedRef.current = false;
    console.log("Reset submission status");
  }, []);

  // Reset all snapshots and create a new session
  const resetSnapshots = useCallback(() => {
    console.log("Resetting all snapshots and creating new session");
    snapshotsRef.current = [];
    isActivelyShownRef.current = {};
    if ((window as any).questionSnapshots) {
      (window as any).questionSnapshots.current = [];
    }
    resultSubmittedRef.current = false;
    sessionStartTimeRef.current = Date.now();
  }, []);

  // Check if result has been submitted
  const isResultSubmitted = useCallback(() => {
    return resultSubmittedRef.current;
  }, []);

  // Set result as submitted
  const markResultAsSubmitted = useCallback(() => {
    resultSubmittedRef.current = true;
    console.log("Marked result as submitted to prevent duplicate submissions");
  }, []);

  // Mark a question as actively shown to the user
  const markQuestionAsActive = useCallback((questionId: string | number, isActive: boolean) => {
    if (!questionId) return;
    
    isActivelyShownRef.current[questionId] = isActive;
    console.log(`Marked question ${questionId} as ${isActive ? 'active' : 'inactive'}`);
  }, []);

  // Backward compatibility for the old method signature
  const addSnapshot = useCallback((question: Question, index: number, userAnswer?: number) => {
    return addOrUpdateSnapshot(question, index, userAnswer);
  }, []); // This will be updated in the next hook declaration

  // Add or update a question snapshot only if it was actually shown to the user
  const addOrUpdateSnapshot = useCallback((question: Question, index: number, answer?: number | null) => {
    if (!question || !question.id) {
      console.warn("Cannot add snapshot: invalid question data");
      return;
    }

    // Create a frozen copy to avoid reference issues
    const frozenQuestion: QuestionSnapshot = JSON.parse(JSON.stringify(question));
    frozenQuestion.shownAtIndex = index;
    
    // Set this question as actively shown
    markQuestionAsActive(question.id, true);
    
    // Only set answer if provided
    if (answer !== undefined) {
      frozenQuestion.userAnswer = answer;
    }
    
    // Find if this question already exists in snapshots
    const existingIndex = snapshotsRef.current.findIndex(q => q.id === question.id);
    
    if (existingIndex === -1) {
      // Add new snapshot
      snapshotsRef.current.push(frozenQuestion);
      console.log(`Added new snapshot for question ${question.id} at index ${index}`);
    } else {
      // Update existing snapshot
      // Preserve the answer if it's not being explicitly updated
      if (answer === undefined && snapshotsRef.current[existingIndex].userAnswer !== undefined) {
        frozenQuestion.userAnswer = snapshotsRef.current[existingIndex].userAnswer;
      }
      snapshotsRef.current[existingIndex] = frozenQuestion;
      console.log(`Updated snapshot for question ${question.id} at index ${index}`);
    }

    // Update the global snapshots store
    if ((window as any).questionSnapshots) {
      (window as any).questionSnapshots.current = [...snapshotsRef.current];
    }
    
    return frozenQuestion;
  }, [markQuestionAsActive]);

  // Update just the answer for a question
  const updateSnapshotAnswer = useCallback((questionId: string | number, answer: number) => {
    const existingIndex = snapshotsRef.current.findIndex(q => q.id === questionId);
    
    if (existingIndex !== -1) {
      snapshotsRef.current[existingIndex].userAnswer = answer;
      
      // Update the global snapshots store
      if ((window as any).questionSnapshots) {
        (window as any).questionSnapshots.current = [...snapshotsRef.current];
      }
      
      console.log(`Updated answer for question ${questionId} to ${answer}`);
      return true;
    }
    
    return false;
  }, []);

  // Get all snapshots
  const getAllSnapshots = useCallback(() => {
    return [...snapshotsRef.current];
  }, []);

  // Filter out duplicates and sort snapshots by their shown index - IMPROVED
  const getSortedSnapshots = useCallback(() => {
    // Only include questions that were answered (with a valid userAnswer)
    // and that were marked as actively shown to the user
    const validSnapshots = snapshotsRef.current.filter(q => 
      q.id !== undefined && 
      q.userAnswer !== undefined && 
      q.userAnswer !== null &&
      (isActivelyShownRef.current[q.id] === true)
    );
    
    console.log(`Found ${validSnapshots.length} valid answered snapshots`);
    
    // Create a map to deduplicate by ID, keeping only the latest entry for each question
    const uniqueMap = new Map<string | number, QuestionSnapshot>();
    
    validSnapshots.forEach(snapshot => {
      if (snapshot.id) {
        uniqueMap.set(snapshot.id, snapshot);
      }
    });
    
    const uniqueSnapshots = Array.from(uniqueMap.values());
    console.log(`After deduplication: ${uniqueSnapshots.length} unique snapshots`);
    
    // Sort by shownAtIndex
    const sortedSnapshots = uniqueSnapshots.sort((a, b) => 
      (a.shownAtIndex !== undefined && b.shownAtIndex !== undefined) 
        ? a.shownAtIndex - b.shownAtIndex 
        : 0
    );
    
    // IMPORTANT: Limit to 10 questions max to prevent extra questions
    const finalSnapshots = sortedSnapshots.slice(0, 10);
    
    console.log("Final snapshots for results:", finalSnapshots.length);
    console.log("Snapshot IDs:", finalSnapshots.map(q => q.id));
    return finalSnapshots;
  }, []);

  // Get the session start time
  const getSessionStartTime = useCallback(() => {
    return new Date(sessionStartTimeRef.current);
  }, []);

  return {
    addOrUpdateSnapshot,
    updateSnapshotAnswer,
    getAllSnapshots,
    getSortedSnapshots,
    resetSnapshots,
    isResultSubmitted,
    markResultAsSubmitted,
    resetSubmissionStatus,
    getSessionStartTime,
    markQuestionAsActive,
    // Backwards compatibility for existing code
    addSnapshot,
  };
}
